import { useMemo } from 'react';
import { z } from 'zod';
import { useQuery, useQueries } from '@tanstack/react-query';
import { supabase, getProdSupabaseClient } from '@vehix/shared/admin/portal-bridge';
import { useAuth } from '@vehix/shared/admin/portal-bridge';
import { useAllTenants } from '@vehix/portal/hooks/useTenants';

// Inline schema for prod.get_current_recall_counts_for_zones_v3.
// NOTE: zone-v3.ts declares this schema too, but importing from there triggers
// a latent runtime bug in adminEnvelope() (uses z.and() which produces a
// ZodIntersection; discriminatedUnion requires ZodObject). Inlining avoids
// that side-effect-on-import. TODO: fix adminEnvelope to use .merge() / .extend()
// and switch back to the canonical schema source.
const recallCountV3RowSchema = z.object({
  zone_id: z.string().uuid(),
  total_recalls_current: z.number().int().nonnegative(),
  data_collection_zones: z.array(z.string()),
  // The cutoff the count was taken at, for this zone's tenant. Not "last crawled" — it
  // can sit weeks back and can move earlier when a zone is reassigned.
  latest_snapshot_date: z.string().nullable(),
  // Oldest crawl still contributing. When it predates latest_snapshot_date the cutoff
  // left a feed behind, so part of the zone's territory is counted from older data or not
  // at all. Optional because the column post-dates this schema; the object is non-strict,
  // so before it was declared here Zod silently stripped it and this page could never
  // show the window.
  oldest_snapshot_date: z.string().nullable().optional()
});
const getCurrentRecallCountsV3ResponseSchema = z.array(recallCountV3RowSchema);
type RecallCountV3Row = z.infer<typeof recallCountV3RowSchema>;
import {
  nextFire,
  sloHours,
  describeCron,
  FALLBACK_SLO_HOURS_CONST,
  FALLBACK_SCHEDULE_DESCRIPTION
} from '@vehix/shared/admin/lib/cronSchedule';
import type {
  AssetStatus,
  DataCollectionScheduleRow,
  FreshnessSLO,
  HealthBannerSummary,
  PerOemAsset,
  RefreshExecutionRow,
  RefreshOutcomeEntry,
  ScheduleContext,
  TenantAssetGroup,
  TenantLookupRow,
  TenantZoneInfo,
  TopIssue,
  VolumeTrendPoint,
  ZoneAssetDetail
} from '@vehix/shared/types/recallSnapshots';

const HOUR_MS = 60 * 60 * 1000;

export const recallSnapshotKeys = {
  all: ['recallSnapshots'] as const,
  countsForTenant: (tenantId: string) => [...recallSnapshotKeys.all, 'counts', tenantId] as const,
  tenantZoneMap: () => [...recallSnapshotKeys.all, 'tenantZoneMap'] as const,
  refreshExecutions: (limit: number) => [...recallSnapshotKeys.all, 'refreshExecutions', limit] as const,
  schedules: () => [...recallSnapshotKeys.all, 'schedules'] as const
};

// ───────────────────────── OEM extraction ─────────────────────────

const KNOWN_OEMS = ['ford', 'stellantis', 'kia', 'hyundai', 'gm', 'general motors'] as const;
const OEM_DISPLAY: Record<string, string> = {
  ford: 'Ford',
  stellantis: 'Stellantis',
  kia: 'Kia',
  hyundai: 'Hyundai',
  gm: 'General Motors',
  'general motors': 'General Motors'
};

/**
 * Extract OEM from a `tenant_refresh_executions.actor_task_name`.
 * Patterns observed in production:
 *   - `crawler/<oem>-<location>` (e.g., `crawler/kia-cincinnati`)
 *   - `kenkebiai/cargurus-<oem>-<state>-<location>` (e.g., `kenkebiai/cargurus-stellantis-ky-lexington`)
 */
export function oemFromTaskName(taskName: string | null | undefined): string | null {
  if (!taskName) return null;
  const lower = taskName.toLowerCase();
  for (const oem of KNOWN_OEMS) {
    const reCrawler = new RegExp(`^crawler/${oem.replace(' ', '\\s')}-`);
    const reCargurus = new RegExp(`/cargurus-${oem.replace(' ', '\\s')}-`);
    if (reCrawler.test(lower) || reCargurus.test(lower)) {
      return OEM_DISPLAY[oem] ?? null;
    }
  }
  return null;
}

/**
 * Extract data-collection zone slug from `actor_task_name`.
 * For `crawler/<slug>` → `<slug>` (e.g., `crawler/kia-cookeville` → `kia-cookeville`).
 * For `kenkebiai/cargurus-<oem>-<state>-<loc>` → `<oem>-<loc>` matching MV's zone_slug.
 */
export function dcZoneFromTaskName(taskName: string | null | undefined): string | null {
  if (!taskName) return null;
  const slashIdx = taskName.indexOf('/');
  if (slashIdx < 0) return null;
  const tail = taskName.slice(slashIdx + 1);
  if (taskName.startsWith('crawler/')) return tail;
  if (taskName.startsWith('kenkebiai/cargurus-')) {
    // cargurus-<oem>-<state>-<loc> → <oem>-<loc>
    const parts = tail.replace(/^cargurus-/, '').split('-');
    if (parts.length >= 3) {
      const oem = parts[0];
      const loc = parts.slice(2).join('-');
      return `${oem}-${loc}`;
    }
  }
  return null;
}

// ───────────────────────── Raw fetchers ─────────────────────────

/**
 * Per-tenant zone+OEM map. Queries prod.tenant_zones JOIN prod.zones directly
 * (one round trip total) so the result includes OEM, which the existing
 * `tenantAPI.getTenantZones` RPC strips out.
 */
export function useTenantZoneMap() {
  const { session, loading: authLoading } = useAuth();
  return useQuery({
    queryKey: recallSnapshotKeys.tenantZoneMap(),
    queryFn: async (): Promise<TenantZoneInfo[]> => {
      const prod = getProdSupabaseClient();
      const [tzRes, zRes] = await Promise.all([
        prod.from('tenant_zones').select('tenant_id, zone_id'),
        prod.from('zones').select('id, zone_name, oem')
      ]);
      if (tzRes.error) throw tzRes.error;
      if (zRes.error) throw zRes.error;
      const zoneById = new Map(
        ((zRes.data ?? []) as Array<{ id: string; zone_name: string | null; oem: string | null }>).map(z => [
          z.id,
          z
        ])
      );
      const rows: TenantZoneInfo[] = [];
      for (const tz of (tzRes.data ?? []) as Array<{ tenant_id: string; zone_id: string }>) {
        const z = zoneById.get(tz.zone_id);
        if (!z) continue;
        rows.push({
          tenant_id: tz.tenant_id,
          zone_id: tz.zone_id,
          zone_name: z.zone_name ?? '',
          oem: z.oem ?? 'Unknown'
        });
      }
      return rows;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!session && !authLoading
  });
}

export function useRecallCountsPerTenant(tenantZones: TenantZoneInfo[], pollMs?: number) {
  const { session, loading: authLoading } = useAuth();
  // Group zones by tenant_id
  const byTenant = useMemo(() => {
    const map = new Map<string, TenantZoneInfo[]>();
    for (const z of tenantZones) {
      const arr = map.get(z.tenant_id) ?? [];
      arr.push(z);
      map.set(z.tenant_id, arr);
    }
    return map;
  }, [tenantZones]);

  const tenantIds = useMemo(() => Array.from(byTenant.keys()), [byTenant]);

  const queries = useQueries({
    queries: tenantIds.map(tenantId => {
      const zones = byTenant.get(tenantId) ?? [];
      const zoneIds = zones.map(z => z.zone_id);
      return {
        queryKey: recallSnapshotKeys.countsForTenant(tenantId),
        queryFn: async (): Promise<RecallCountV3Row[]> => {
          if (zoneIds.length === 0) return [];
          const { data, error } = await supabase.rpc('get_current_recall_counts_for_zones_v3', {
            p_zone_ids: zoneIds,
            p_tenant_id: tenantId
          });
          if (error) throw error;
          return getCurrentRecallCountsV3ResponseSchema.parse(data ?? []);
        },
        staleTime: 60 * 1000,
        refetchInterval: pollMs ?? 120_000,
        refetchOnWindowFocus: true,
        enabled: !!session && !authLoading && zoneIds.length > 0
      };
    })
  });

  const data = useMemo(() => {
    const map = new Map<string, RecallCountV3Row[]>();
    queries.forEach((q, idx) => {
      const tenantId = tenantIds[idx];
      if (tenantId && q.data) map.set(tenantId, q.data);
    });
    return map;
  }, [queries, tenantIds]);

  return {
    data,
    isLoading: queries.some(q => q.isLoading),
    isError: queries.some(q => q.isError)
  };
}

export function useRefreshExecutions(limit = 2000, pollMs?: number) {
  const { session, loading: authLoading } = useAuth();
  return useQuery({
    queryKey: recallSnapshotKeys.refreshExecutions(limit),
    queryFn: async (): Promise<RefreshExecutionRow[]> => {
      const prod = getProdSupabaseClient();
      const { data, error } = await prod
        .from('tenant_refresh_executions')
        .select(
          'id, tenant_id, execution_date, actor_task_name, trigger_source, schedule_id, ' +
            'data_collection_status, data_collection_started_at, data_collection_finished_at, data_collection_duration_seconds, ' +
            'vin_processing_status, vin_processing_started_at, vin_processing_finished_at, vin_processing_duration_seconds, ' +
            'overall_status, total_duration_seconds, raw_records_from_apify, staging_inserted_records, snapshot_inserted_records, snapshot_updated_records, ' +
            'vins_processed, total_recalls_found, error_summary, created_at, updated_at'
        )
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as unknown as RefreshExecutionRow[];
    },
    staleTime: 60 * 1000,
    refetchInterval: pollMs ?? 60_000,
    enabled: !!session && !authLoading
  });
}

export function useDataCollectionSchedules() {
  const { session, loading: authLoading } = useAuth();
  return useQuery({
    queryKey: recallSnapshotKeys.schedules(),
    queryFn: async (): Promise<DataCollectionScheduleRow[]> => {
      const prod = getProdSupabaseClient();
      const { data, error } = await prod
        .from('data_collection_schedules')
        .select(
          'id, schedule_name, task_name, tenant_id, cron_expression, time_zone, is_enabled, ' +
            'last_triggered_at, last_run_status, run_count, description'
        )
        .is('deleted_at', null);
      if (error) throw error;
      return (data ?? []) as unknown as DataCollectionScheduleRow[];
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!session && !authLoading
  });
}

// ───────────────────────── Derived selectors ─────────────────────────

interface ExecutionAggregateForOem {
  latest_at: string | null;
  latest_inserts: number | null;
  latest_total_recalls: number | null;
  prev_total_recalls: number | null;
}

function statusFor(opts: {
  delta_pct: number | null;
  hours_since_refresh: number | null;
  slo_hrs: number;
}): AssetStatus {
  if (opts.hours_since_refresh != null && opts.hours_since_refresh > opts.slo_hrs) return 'stale';
  if (opts.delta_pct != null && opts.delta_pct <= -25) return 'critical';
  if (opts.delta_pct != null && opts.delta_pct <= -10) return 'warning';
  return 'healthy';
}

const STATUS_RANK: Record<AssetStatus, number> = { healthy: 0, warning: 1, stale: 2, critical: 3 };
function worstStatus(a: AssetStatus, b: AssetStatus): AssetStatus {
  return (STATUS_RANK[a] ?? 0) >= (STATUS_RANK[b] ?? 0) ? a : b;
}

interface AssetTableInputs {
  tenants: TenantLookupRow[];
  tenantZones: TenantZoneInfo[];
  countsByTenant: Map<string, RecallCountV3Row[]>;
  executions: RefreshExecutionRow[];
  schedules: DataCollectionScheduleRow[];
}

/**
 * Master aggregator: builds the per-(tenant, OEM) asset rows used by Section H,
 * KPI strip, Top Issues, and Health Banner.
 */
export function buildAssetTable(inputs: AssetTableInputs): TenantAssetGroup[] {
  const { tenants, tenantZones, countsByTenant, executions, schedules } = inputs;

  // Group zones by (tenant_id, oem)
  const zonesByTenantOem = new Map<string, Map<string, TenantZoneInfo[]>>();
  for (const z of tenantZones) {
    const oemMap = zonesByTenantOem.get(z.tenant_id) ?? new Map<string, TenantZoneInfo[]>();
    const arr = oemMap.get(z.oem) ?? [];
    arr.push(z);
    oemMap.set(z.oem, arr);
    zonesByTenantOem.set(z.tenant_id, oemMap);
  }

  // Aggregate executions per (tenant, oem) — latest two completed runs to derive prev/cur recalls
  const execByTenantOem = new Map<string, ExecutionAggregateForOem>();
  for (const [tenantId, oemMap] of zonesByTenantOem.entries()) {
    for (const oem of oemMap.keys()) {
      const matching = executions
        .filter(e => e.tenant_id === tenantId && oemFromTaskName(e.actor_task_name) === oem)
        .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
      const latest = matching[0];
      const prev = matching.find(
        e => e !== latest && (e.overall_status === 'completed' || e.overall_status === 'data_collection')
      );
      execByTenantOem.set(`${tenantId}::${oem}`, {
        latest_at: latest?.created_at ?? null,
        latest_inserts: latest?.snapshot_inserted_records ?? null,
        latest_total_recalls: latest?.total_recalls_found ?? null,
        prev_total_recalls: prev?.total_recalls_found ?? null
      });
    }
  }

  // Tenant-level slo: use the tenant's most-common cron, fall back to constant
  const sloByTenant = new Map<string, number>();
  for (const tenantId of zonesByTenantOem.keys()) {
    const tSchedules = schedules.filter(s => s.tenant_id === tenantId && s.is_enabled);
    if (tSchedules.length === 0) {
      sloByTenant.set(tenantId, FALLBACK_SLO_HOURS_CONST);
      continue;
    }
    // Pick the schedule with the largest gap → most permissive SLO
    const allHrs = tSchedules.map(s => sloHours(s.cron_expression));
    sloByTenant.set(tenantId, Math.max(...allHrs));
  }

  const groups: TenantAssetGroup[] = [];
  for (const tenant of tenants) {
    const oemMap = zonesByTenantOem.get(tenant.id);
    if (!oemMap) continue;
    const counts = countsByTenant.get(tenant.id) ?? [];
    const slo = sloByTenant.get(tenant.id) ?? FALLBACK_SLO_HOURS_CONST;

    const oems: PerOemAsset[] = [];
    for (const [oem, zones] of oemMap.entries()) {
      const zoneIds = new Set(zones.map(z => z.zone_id));
      const oemCounts = counts.filter(c => zoneIds.has(c.zone_id));
      const current_count = oemCounts.reduce((acc, c) => acc + (c.total_recalls_current ?? 0), 0);
      const latest_snapshot_date = oemCounts
        .map(c => c.latest_snapshot_date)
        .filter((d): d is string => !!d)
        .sort()
        .pop() ?? null;

      const exec = execByTenantOem.get(`${tenant.id}::${oem}`);
      const last_refresh_at = exec?.latest_at ?? null;
      const hours_since_refresh =
        last_refresh_at != null
          ? Math.round((Date.now() - new Date(last_refresh_at).getTime()) / HOUR_MS)
          : null;
      // total_recalls_found is unreliable (almost always 0 in this dataset),
      // so only treat it as a real previous-count when > 0. Otherwise show "—"
      // rather than fabricated zeros that misrepresent the data state.
      const rawPrev = exec?.prev_total_recalls ?? null;
      const previous_count = rawPrev != null && rawPrev > 0 ? rawPrev : null;
      const delta_count = previous_count != null ? current_count - previous_count : null;
      const delta_pct =
        previous_count != null && previous_count > 0
          ? Math.round(((current_count - previous_count) / previous_count) * 10_000) / 100
          : null;
      // snapshot_inserted_records: 0 may mean "no inserts" OR "unpopulated".
      // Only display when > 0 so the column reads honestly.
      const rawAdds = exec?.latest_inserts ?? null;
      const additions = rawAdds != null && rawAdds > 0 ? rawAdds : null;
      const removals =
        additions != null && delta_count != null ? Math.max(0, additions - delta_count) : null;

      const zones_in_oem: ZoneAssetDetail[] = zones.map(z => {
        const c = oemCounts.find(x => x.zone_id === z.zone_id);
        return {
          zone_id: z.zone_id,
          zone_name: z.zone_name,
          count: c?.total_recalls_current ?? 0,
          latest_snapshot_date: c?.latest_snapshot_date ?? null,
          data_collection_zones: c?.data_collection_zones ?? []
        };
      });

      oems.push({
        tenant_id: tenant.id,
        tenant_name: tenant.tenant_name,
        oem,
        current_count,
        previous_count,
        delta_count,
        delta_pct,
        additions,
        removals,
        latest_snapshot_date,
        last_refresh_at,
        hours_since_refresh,
        status: statusFor({ delta_pct, hours_since_refresh, slo_hrs: slo }),
        zones_in_oem
      });
    }

    if (oems.length === 0) continue;
    const worst = oems.reduce<AssetStatus>((acc, o) => worstStatus(acc, o.status), 'healthy');
    const last_refresh_at = oems
      .map(o => o.last_refresh_at)
      .filter((d): d is string => !!d)
      .sort()
      .pop() ?? null;

    groups.push({
      tenant_id: tenant.id,
      tenant_name: tenant.tenant_name,
      oems: oems.sort((a, b) => a.oem.localeCompare(b.oem)),
      worst_status: worst,
      last_refresh_at
    });
  }

  return groups.sort(
    (a, b) => (STATUS_RANK[b.worst_status] ?? 0) - (STATUS_RANK[a.worst_status] ?? 0)
  );
}

/** Combined hook: pulls all raw data, returns the assembled asset table. */
export function usePerTenantAssetTable(pollMs?: number) {
  const tenantsQuery = useAllTenants();
  const tenantZoneMap = useTenantZoneMap();
  const counts = useRecallCountsPerTenant(tenantZoneMap.data ?? [], pollMs);
  const executions = useRefreshExecutions(2000, pollMs);
  const schedules = useDataCollectionSchedules();

  const groups = useMemo(() => {
    if (!tenantsQuery.data || !tenantZoneMap.data || !executions.data || !schedules.data) return [];
    const lookup: TenantLookupRow[] = tenantsQuery.data.map(t => ({
      id: t.id,
      tenant_name: t.tenant_name ?? '',
      tenant_slug: t.tenant_slug ?? ''
    }));
    return buildAssetTable({
      tenants: lookup,
      tenantZones: tenantZoneMap.data,
      countsByTenant: counts.data,
      executions: executions.data,
      schedules: schedules.data
    });
  }, [tenantsQuery.data, tenantZoneMap.data, counts.data, executions.data, schedules.data]);

  return {
    data: groups,
    isLoading:
      tenantsQuery.isLoading ||
      tenantZoneMap.isLoading ||
      counts.isLoading ||
      executions.isLoading ||
      schedules.isLoading,
    isError:
      tenantsQuery.isError ||
      tenantZoneMap.isError ||
      counts.isError ||
      executions.isError ||
      schedules.isError,
    refresh: () => {
      executions.refetch();
      schedules.refetch();
    }
  };
}

export function useHealthBannerSummary(groups: TenantAssetGroup[], executions: RefreshExecutionRow[]): HealthBannerSummary {
  return useMemo(() => {
    let healthy = 0;
    let warning = 0;
    let critical = 0;
    let stale = 0;
    for (const g of groups) {
      for (const o of g.oems) {
        if (o.status === 'healthy') healthy++;
        else if (o.status === 'warning') warning++;
        else if (o.status === 'critical') critical++;
        else if (o.status === 'stale') stale++;
      }
    }
    const sevenDaysAgo = Date.now() - 7 * 24 * HOUR_MS;
    const failed_runs_last_7d = executions.filter(
      e =>
        e.overall_status === 'failed' &&
        e.created_at &&
        new Date(e.created_at).getTime() >= sevenDaysAgo
    ).length;
    const total_assets = healthy + warning + critical + stale;
    const overall_status: AssetStatus =
      critical > 0 || failed_runs_last_7d > 0 ? 'critical' : stale > 0 ? 'stale' : warning > 0 ? 'warning' : 'healthy';
    return { total_assets, healthy, warning, critical, stale, failed_runs_last_7d, overall_status };
  }, [groups, executions]);
}

export function useFreshnessSLO(executions: RefreshExecutionRow[], schedules: DataCollectionScheduleRow[]): FreshnessSLO {
  return useMemo(() => {
    const last = executions
      .map(e => e.created_at)
      .filter((d): d is string => !!d)
      .sort()
      .pop() ?? null;
    const hours_since_refresh =
      last != null ? Math.round((Date.now() - new Date(last).getTime()) / HOUR_MS) : null;
    const slo_hrs = schedules.length > 0
      ? Math.max(...schedules.map(s => sloHours(s.cron_expression)))
      : FALLBACK_SLO_HOURS_CONST;
    const pct_of_slo = hours_since_refresh != null ? Math.round((hours_since_refresh / slo_hrs) * 100) : 0;
    return {
      last_refresh_at: last,
      hours_since_refresh,
      slo_hours: slo_hrs,
      pct_of_slo,
      within_slo: hours_since_refresh == null ? true : hours_since_refresh <= slo_hrs
    };
  }, [executions, schedules]);
}

export function useScheduleContext(schedules: DataCollectionScheduleRow[], executions: RefreshExecutionRow[]): ScheduleContext {
  return useMemo(() => {
    if (schedules.length === 0) {
      return {
        cron_expression: '',
        time_zone: 'America/Chicago',
        human_readable: FALLBACK_SCHEDULE_DESCRIPTION,
        next_fire_at: null,
        recent_runs: [],
        workflow_management_url: '/workflow/management',
        is_dynamic: false
      };
    }
    // Use the most-frequent cron expression as the "primary"
    const counts = new Map<string, number>();
    schedules.forEach(s => counts.set(s.cron_expression, (counts.get(s.cron_expression) ?? 0) + 1));
    const primary = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
    const primarySchedule = primary
      ? schedules.find(s => s.cron_expression === primary[0])
      : undefined;
    if (!primarySchedule) {
      return {
        cron_expression: '',
        time_zone: 'America/Chicago',
        human_readable: FALLBACK_SCHEDULE_DESCRIPTION,
        next_fire_at: null,
        recent_runs: [],
        workflow_management_url: '/workflow/management',
        is_dynamic: false
      };
    }
    const next = nextFire(primarySchedule.cron_expression, primarySchedule.time_zone);

    const recent_runs = executions
      .slice(0, 4)
      .map(e => ({
        status: (e.overall_status === 'completed'
          ? 'completed'
          : e.overall_status === 'failed'
          ? 'failed'
          : e.overall_status === 'running' || e.overall_status === 'data_collection' || e.overall_status === 'vin_processing'
          ? 'running'
          : 'other') as ScheduleContext['recent_runs'][number]['status'],
        at: e.created_at
      }));

    return {
      cron_expression: primarySchedule.cron_expression,
      time_zone: primarySchedule.time_zone,
      human_readable: describeCron(primarySchedule.cron_expression, primarySchedule.time_zone),
      next_fire_at: next,
      recent_runs,
      workflow_management_url: '/workflow/management',
      is_dynamic: true
    };
  }, [schedules, executions]);
}

export function useVolumeTrendWithBand(
  executions: RefreshExecutionRow[],
  rolling = 4
): VolumeTrendPoint[] {
  return useMemo(() => {
    // Aggregate by (execution_date, OEM)
    const buckets = new Map<string, { oem: string; date: string; total: number }>();
    for (const e of executions) {
      const oem = oemFromTaskName(e.actor_task_name);
      if (!oem || !e.execution_date || e.total_recalls_found == null) continue;
      const key = `${e.execution_date}::${oem}`;
      const existing = buckets.get(key);
      if (existing) {
        existing.total += e.total_recalls_found;
      } else {
        buckets.set(key, { oem, date: e.execution_date, total: e.total_recalls_found });
      }
    }
    const rows = [...buckets.values()].sort((a, b) => a.date.localeCompare(b.date));
    const byOem = new Map<string, typeof rows>();
    for (const r of rows) {
      const arr = byOem.get(r.oem) ?? [];
      arr.push(r);
      byOem.set(r.oem, arr);
    }
    const out: VolumeTrendPoint[] = [];
    for (const [oem, series] of byOem.entries()) {
      for (let i = 0; i < series.length; i++) {
        const point = series[i];
        if (!point) continue;
        const window = series.slice(Math.max(0, i - rolling), i);
        if (window.length < rolling) {
          out.push({
            execution_date: point.date,
            oem,
            total_recalls: point.total,
            baseline_mean: null,
            baseline_lower: null,
            baseline_upper: null,
            is_outlier: false
          });
          continue;
        }
        const mean = window.reduce((a, w) => a + w.total, 0) / window.length;
        const variance = window.reduce((a, w) => a + (w.total - mean) ** 2, 0) / window.length;
        const sigma = Math.sqrt(variance);
        const lower = mean - 2 * sigma;
        const upper = mean + 2 * sigma;
        const t = point.total;
        out.push({
          execution_date: point.date,
          oem,
          total_recalls: t,
          baseline_mean: Math.round(mean),
          baseline_lower: Math.round(lower),
          baseline_upper: Math.round(upper),
          is_outlier: t < lower || t > upper
        });
      }
    }
    return out.sort((a, b) => a.execution_date.localeCompare(b.execution_date));
  }, [executions, rolling]);
}

export function useRefreshOutcomeRibbon(
  executions: RefreshExecutionRow[],
  tenantsLookup: Map<string, string>,
  limit = 20
): RefreshOutcomeEntry[] {
  return useMemo(() => {
    return executions.slice(0, limit).map(e => ({
      id: e.id,
      tenant_id: e.tenant_id,
      tenant_name: tenantsLookup.get(e.tenant_id) ?? e.tenant_id,
      oem: oemFromTaskName(e.actor_task_name),
      task_name: e.actor_task_name,
      status: (e.overall_status === 'completed'
        ? 'completed'
        : e.overall_status === 'failed'
        ? 'failed'
        : e.overall_status === 'running' || e.overall_status === 'data_collection' || e.overall_status === 'vin_processing'
        ? 'running'
        : 'other') as RefreshOutcomeEntry['status'],
      started_at: e.data_collection_started_at ?? e.created_at,
      duration_seconds: e.total_duration_seconds,
      error_summary: e.error_summary
    }));
  }, [executions, tenantsLookup, limit]);
}

export function useTopIssues(groups: TenantAssetGroup[]): TopIssue[] {
  return useMemo(() => {
    const issues: TopIssue[] = [];
    for (const g of groups) {
      for (const o of g.oems) {
        if (o.status === 'critical' || o.status === 'warning' || o.status === 'stale') {
          const reason =
            o.status === 'stale'
              ? `Stale: ${o.hours_since_refresh ?? '?'}h since last refresh`
              : `${o.delta_pct?.toFixed(1) ?? '?'}% drop vs prior snapshot`;
          issues.push({
            id: `${g.tenant_id}::${o.oem}`,
            severity: o.status === 'critical' || o.status === 'stale' ? 'critical' : 'warning',
            tenant_id: g.tenant_id,
            tenant_name: g.tenant_name,
            oem: o.oem,
            reason,
            occurred_at: o.last_refresh_at
          });
        }
      }
    }
    // Critical first, then warning; within each, most recent first
    return issues.sort((a, b) => {
      const sevDiff = (a.severity === 'critical' ? 0 : 1) - (b.severity === 'critical' ? 0 : 1);
      if (sevDiff !== 0) return sevDiff;
      const at = a.occurred_at ?? '';
      const bt = b.occurred_at ?? '';
      return at < bt ? 1 : -1;
    });
  }, [groups]);
}
